import { useState, useEffect, useMemo, useCallback } from 'react';
import { Check, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ExtractionResult } from './VisibleBreakdownScreen';

/* ═══════════════════════════════════════════════════════════════════
 * ProcessingScreen — Dedicated full-screen analysis feed
 *
 * Phase 1: API in-flight → generic waiting steps (timed)
 * Phase 2: Result arrives → real insights revealed sequentially
 * Phase 3: onComplete callback fires → parent transitions
 * ═══════════════════════════════════════════════════════════════════ */

interface FeedItem {
  label: string;
  detail?: string;
  type: 'progress' | 'insight' | 'warning';
}

interface ProcessingScreenProps {
  /** null while API is in-flight, populated when done */
  extraction: ExtractionResult | null;
  hasFiles: boolean;
  onComplete: () => void;
  inputQuality?: 'high' | 'medium' | 'low';
}

export const ProcessingScreen = ({ extraction, hasFiles, onComplete, inputQuality }: ProcessingScreenProps) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const [phase, setPhase] = useState<'waiting' | 'revealing'>('waiting');

  // ── Phase 1: generic waiting steps ──
  const waitingSteps: string[] = useMemo(() => {
    const base = ['Dein Input wird analysiert'];
    if (hasFiles) base.push('Dokumente werden verarbeitet');
    base.push('Kontext wird strukturiert');
    return base;
  }, [hasFiles]);

  // ── Phase 2: real feed items from extraction ──
  const feedItems: FeedItem[] = useMemo(() => {
    if (!extraction) return [];
    const items: FeedItem[] = [];
    const f = extraction.fields;

    const optA = f.optionA?.value as string | undefined;
    const optB = f.optionB?.value as string | undefined;
    if (optA && optB) {
      items.push({
        label: 'Zwei Entscheidungsrichtungen erkannt',
        detail: `${truncate(optA, 50)} vs. ${truncate(optB, 50)}`,
        type: 'insight',
      });
    } else if (optA || optB) {
      items.push({
        label: 'Entscheidungsrichtung erkannt',
        detail: truncate((optA || optB)!, 60),
        type: 'insight',
      });
    }

    const conflict = f.coreConflict?.value as string | undefined;
    const stakes = f.stakesInaction?.value as string | undefined;
    if (conflict) {
      items.push({ label: 'Zielkonflikt aufgebaut', detail: truncate(conflict, 70), type: 'insight' });
    } else if (stakes) {
      items.push({ label: 'Einsatz identifiziert', detail: truncate(stakes, 70), type: 'insight' });
    }

    const risks = extraction.derived?.key_risks;
    if (risks && risks.length > 0) {
      items.push({
        label: `${risks.length} Risiko${risks.length > 1 ? 'faktoren' : ''} identifiziert`,
        detail: truncate(risks[0], 65),
        type: 'insight',
      });
    }

    const assumptions = extraction.derived?.core_assumptions;
    if (assumptions && assumptions.length > 0) {
      items.push({
        label: `${assumptions.length} Annahme${assumptions.length > 1 ? 'n' : ''} erkannt`,
        detail: truncate(assumptions[0], 65),
        type: 'insight',
      });
    }

    const missing = extraction.missing_fields;
    if (missing && missing.length > 0) {
      items.push({
        label: `${missing.length} unklare${missing.length > 1 ? ' Bereiche' : 'r Bereich'} erkannt`,
        detail: 'Werden im nächsten Schritt geklärt',
        type: 'warning',
      });
    }

    const cl = extraction.classification;
    if (cl?.suggested_title) {
      items.push({
        label: 'Entscheidung klassifiziert',
        detail: truncate(cl.suggested_title, 60),
        type: 'insight',
      });
    }

    return items;
  }, [extraction]);

  // ── Phase 1: show waiting steps with timing ──
  useEffect(() => {
    if (phase !== 'waiting') return;
    setVisibleCount(1);
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 1; i < waitingSteps.length; i++) {
      timers.push(setTimeout(() => setVisibleCount(i + 1), i * 1200));
    }
    return () => timers.forEach(clearTimeout);
  }, [phase, waitingSteps.length]);

  // ── Transition: extraction arrives → reveal phase ──
  useEffect(() => {
    if (extraction && phase === 'waiting') {
      const t = setTimeout(() => {
        setPhase('revealing');
        setVisibleCount(0);
      }, 400);
      return () => clearTimeout(t);
    }
  }, [extraction, phase]);

  // ── Phase 2: reveal real items sequentially ──
  const stableOnComplete = useCallback(onComplete, [onComplete]);

  useEffect(() => {
    if (phase !== 'revealing' || feedItems.length === 0) return;

    const timers: ReturnType<typeof setTimeout>[] = [];

    for (let i = 0; i <= feedItems.length; i++) {
      timers.push(setTimeout(() => {
        setVisibleCount(i);
        if (i === feedItems.length) {
          timers.push(setTimeout(stableOnComplete, 1100));
        }
      }, i * 600));
    }

    return () => timers.forEach(clearTimeout);
  }, [phase, feedItems.length, stableOnComplete]);

  // ── Render ──
  const isWaiting = phase === 'waiting';
  const items = isWaiting ? waitingSteps : feedItems;

  return (
    <div className="w-full min-h-[60vh] flex flex-col items-center justify-center px-4 py-8 animate-fade-in">
      <div className="w-full max-w-[520px] flex flex-col">

        {/* ── Screen headline ── */}
        <div className="mb-10 text-center">
          <h2 className="text-[22px] md:text-[26px] font-serif font-semibold tracking-[-0.01em] text-foreground leading-[1.3]">
            {isWaiting ? 'Dein Input wird analysiert' : 'Wir strukturieren deine Entscheidung'}
          </h2>
        </div>

        {/* ── Analysis Feed ── */}
        <div className="space-y-1">
          {items.map((item, i) => {
            const isVisible = i < visibleCount;
            const isLatest = i === visibleCount - 1;
            const isDone = i < visibleCount - 1;

            const label = typeof item === 'string' ? item : item.label;
            const detail = typeof item === 'string' ? undefined : item.detail;
            const itemType = typeof item === 'string' ? 'progress' : item.type;

            return (
              <div
                key={`${phase}-${i}`}
                className={cn(
                  'flex items-start gap-3.5 py-2.5 transition-all duration-500',
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-3 pointer-events-none h-0 overflow-hidden',
                  isDone && 'opacity-50',
                )}
              >
                {/* Status dot / check */}
                <div className={cn(
                  'w-6 h-6 rounded-lg flex items-center justify-center shrink-0 mt-0.5 transition-all duration-500',
                  isDone
                    ? 'bg-primary/10'
                    : isLatest
                      ? 'bg-primary/8'
                      : 'bg-transparent',
                )}>
                  {isDone ? (
                    <Check className="h-3.5 w-3.5 text-primary" />
                  ) : isLatest ? (
                    <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  ) : itemType === 'warning' && isVisible ? (
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                  ) : null}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <span className={cn(
                    'text-[14px] block transition-colors duration-300 leading-snug',
                    isLatest ? 'text-foreground font-medium' : 'text-muted-foreground',
                    itemType === 'warning' && isLatest && 'text-amber-600 dark:text-amber-400',
                  )}>
                    {label}
                  </span>
                  {detail && isVisible && (
                    <span className={cn(
                      'text-[12.5px] block mt-1 transition-all duration-500 leading-relaxed',
                      isLatest ? 'text-foreground/45' : 'text-muted-foreground/50',
                      itemType === 'warning' && 'text-amber-500/50 dark:text-amber-400/50',
                    )}>
                      → {detail}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Low quality hint ── */}
        {inputQuality === 'low' && phase === 'revealing' && visibleCount >= feedItems.length && (
          <div className="mt-6 text-center animate-fade-in">
            <p className="text-[12px] text-amber-600/60 dark:text-amber-400/50">
              Dein Input war knapp. Prüfe die erkannten Felder besonders sorgfältig.
            </p>
          </div>
        )}

        {/* ── Subtle progress bar ── */}
        <div className="mt-10 w-full h-[2px] rounded-full bg-border/30 overflow-hidden">
          <div
            className="h-full bg-primary/30 rounded-full transition-all duration-700 ease-out"
            style={{
              width: isWaiting
                ? `${Math.min((visibleCount / waitingSteps.length) * 60, 60)}%`
                : `${60 + (visibleCount / Math.max(feedItems.length, 1)) * 40}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

function truncate(str: string, max: number): string {
  if (str.length <= max) return str;
  return str.slice(0, max - 1).trimEnd() + '…';
}
